<template>
  <div class="predefined-tests-page">
    <!-- Header -->
    <div class="page-header">
      <div class="container-fluid">
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <h1 class="page-title">
              {{ $t("qbank.new_test.predefined_tests") }}
            </h1>
            <p class="page-subtitle">
              {{ $t("qbank.new_test.predefined_subtitle") }}
            </p>
          </div>
          <button @click="$router.go(-1)" class="btn btn-back">
            <i class="fas fa-arrow-left me-2"></i>
            {{ $t("common.back") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="filters-section">
      <div class="container-fluid">
        <!-- Type Filter Chips -->
        <div class="type-chips">
          <button
            class="type-chip"
            :class="{ active: selectedType === null }"
            @click="selectType(null)"
          >
            {{ $t("common.all") }}
          </button>
          <button
            v-for="type in predefinedTypes"
            :key="type.id"
            class="type-chip"
            :class="{ active: selectedType === type.id }"
            @click="selectType(type.id)"
          >
            {{ getTypeName(type) }}
          </button>
        </div>

        <!-- Dropdown Filters -->
        <div class="dropdown-filters">
          <div class="filter-group">
            <select
              v-model="selectedSource"
              class="filter-select"
              @change="applyFilters"
            >
              <option :value="null">
                {{ $t("qbank.new_test.all_sources") }}
              </option>
              <option
                v-for="source in sources"
                :key="source.id"
                :value="source.id"
              >
                {{ source.name }}
              </option>
            </select>
          </div>
          <div class="filter-group">
            <select
              v-model="selectedCategory"
              class="filter-select"
              @change="applyFilters"
            >
              <option :value="null">
                {{ $t("qbank.new_test.all_categories") }}
              </option>
              <option
                v-for="category in categories"
                :key="category.id"
                :value="category.id"
              >
                {{ category.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="content-section">
      <div class="container-fluid">
        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <div class="spinner-border text-success" role="status">
            <span class="visually-hidden">{{ $t("common.loading") }}</span>
          </div>
          <p class="mt-3">{{ $t("common.loading") }}</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="predefinedQBanks.length === 0" class="empty-state">
          <div class="empty-icon">
            <i class="fas fa-clipboard-list"></i>
          </div>
          <h4>{{ $t("qbank.new_test.no_predefined_tests") }}</h4>
          <p>{{ $t("qbank.new_test.no_predefined_hint") }}</p>
        </div>

        <!-- QBanks Grid -->
        <div v-else class="qbanks-grid">
          <div
            v-for="qbank in predefinedQBanks"
            :key="qbank.id"
            class="qbank-card"
            :class="{ 'is-used': qbank.is_used }"
            @click="handleCardClick(qbank)"
          >
            <div class="card-header">
              <div class="card-header-content">
                <h5 class="qbank-name">{{ qbank.name }}</h5>
                <!-- Used Badge -->
                <div v-if="qbank.is_used" class="used-badge">
                  <i class="fas fa-check-circle"></i>
                  {{ $t("qbank.new_test.already_used") || "تم استخدامه" }}
                </div>
              </div>
              <span class="questions-badge">
                {{ qbank.questions_count }} {{ $t("qbank.questions") }}
              </span>
            </div>
            <div class="card-body">
              <div class="qbank-meta">
                <span class="type-badge">{{ getTypeLabel(qbank) }}</span>
                <span v-if="qbank.child_category" class="category-text">
                  {{ qbank.child_category.title }}
                </span>
              </div>
              <div
                v-if="qbank.sources && qbank.sources.length > 0"
                class="sources-list"
              >
                <span
                  v-for="source in qbank.sources"
                  :key="source.id"
                  class="source-tag"
                >
                  {{ source.name }}
                </span>
              </div>
            </div>
            <div class="card-footer">
              <button class="btn btn-start">
                <i class="fas fa-play me-2"></i>
                {{ $t("qbank.new_test.start_test") }}
              </button>
            </div>
          </div>
        </div>

        <!-- Load More -->
        <div v-if="hasMorePages && !loading" class="load-more-container">
          <button
            class="btn btn-load-more"
            :disabled="loadingMore"
            @click="loadMore"
          >
            <span
              v-if="loadingMore"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            {{ $t("qbank.new_test.load_more") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Clone Confirmation Modal -->
    <div
      class="modal fade"
      id="cloneConfirmModal"
      tabindex="-1"
      ref="cloneModalRef"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content clone-modal">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t("common.confirm") }}</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
            ></button>
          </div>
          <div class="modal-body text-center">
            <div class="confirm-icon">
              <i class="fas fa-clipboard-check"></i>
            </div>
            <p class="confirm-text">
              {{ $t("qbank.new_test.clone_confirm") }}
              <strong>"{{ selectedQBank?.name }}"</strong>?
            </p>
            <div class="qbank-details" v-if="selectedQBank">
              <span class="detail-item">
                <i class="fas fa-question-circle"></i>
                {{ selectedQBank.questions_count }} {{ $t("qbank.questions") }}
              </span>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-cancel"
              data-bs-dismiss="modal"
            >
              {{ $t("common.cancel") }}
            </button>
            <button
              type="button"
              class="btn btn-confirm"
              :disabled="cloning"
              @click="cloneQBank"
            >
              <span
                v-if="cloning"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              <i v-else class="fas fa-play me-2"></i>
              {{ $t("qbank.new_test.start_test") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useQBankStore } from "@/stores/qbank";
import Swal from "sweetalert2";
import i18n from "@/plugins/i18n";

export default {
  name: "PredefinedTests",
  data() {
    return {
      loading: true,
      loadingMore: false,
      cloning: false,
      selectedType: null,
      selectedSource: null,
      selectedCategory: null,
      selectedQBank: null,
      cloneModalInstance: null,
      currentLocale: i18n.global.locale.value,
    };
  },
  computed: {
    subscriptionPlanId() {
      return this.$route.query.subscriptionId;
    },
    store() {
      return useQBankStore();
    },
    predefinedTypes() {
      return this.store.predefinedTypes;
    },
    sources() {
      return this.store.sources;
    },
    categories() {
      return this.store.categories;
    },
    predefinedQBanks() {
      return this.store.predefinedQBanks;
    },
    pagination() {
      return this.store.predefinedQBanksPagination;
    },
    hasMorePages() {
      return this.pagination.currentPage < this.pagination.lastPage;
    },
    isArabic() {
      return this.currentLocale === "ar";
    },
  },
  async mounted() {
    if (!this.subscriptionPlanId) {
      this.$router.push("/qbank");
      return;
    }

    this.store.setSubscriptionPlanId(this.subscriptionPlanId);

    try {
      await Promise.all([
        this.store.loadPredefinedTypes(),
        this.store.loadSources(),
        this.store.loadCategories(null, { predefined: true }),
        this.store.loadPredefinedQBanks(),
      ]);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      this.loading = false;
    }
  },
  watch: {
    "$i18n.locale"(newLocale) {
      this.currentLocale = newLocale;
    },
  },
  methods: {
    getTypeLabel(qbank) {
      return this.isArabic
        ? qbank.predefined_type_arabic || qbank.predefined_type_label
        : qbank.predefined_type_label;
    },
    getTypeName(type) {
      return this.isArabic ? type.name_ar || type.name : type.name;
    },
    selectType(typeId) {
      this.selectedType = typeId;
      this.applyFilters();
    },
    async applyFilters() {
      this.loading = true;
      try {
        await this.store.loadPredefinedQBanks({
          predefined_type: this.selectedType,
          source_id: this.selectedSource,
          child_category_id: this.selectedCategory,
        });
      } catch (error) {
        console.error("Failed to apply filters:", error);
      } finally {
        this.loading = false;
      }
    },
    async loadMore() {
      this.loadingMore = true;
      try {
        await this.store.loadPredefinedQBanks(
          {
            predefined_type: this.selectedType,
            source_id: this.selectedSource,
            child_category_id: this.selectedCategory,
            page: this.pagination.currentPage + 1,
          },
          true // append
        );
      } catch (error) {
        console.error("Failed to load more:", error);
      } finally {
        this.loadingMore = false;
      }
    },
    handleCardClick(qbank) {
      if (qbank.is_used) {
        // Show message that test has been used before
        Swal.fire({
          icon: "info",
          title:
            this.$t("qbank.new_test.test_used_title") ||
            "تم استخدام هذا الاختبار",
          text:
            this.$t("qbank.new_test.test_used_message") ||
            "هذا الاختبار تم استخدامه من قبل. يمكنك مراجعته من قائمة اختباراتي.",
          confirmButtonText: this.$t("common.ok") || "حسناً",
          confirmButtonColor: "#10b981",
        });
        return;
      }
      this.showCloneConfirmation(qbank);
    },
    showCloneConfirmation(qbank) {
      this.selectedQBank = qbank;
      if (!this.cloneModalInstance) {
        this.cloneModalInstance = new bootstrap.Modal(this.$refs.cloneModalRef);
      }
      this.cloneModalInstance.show();
    },
    async cloneQBank() {
      if (!this.selectedQBank) return;

      this.cloning = true;
      try {
        const response = await this.store.cloneQBank(this.selectedQBank.id);

        // Close modal
        this.cloneModalInstance.hide();

        // Get the cloned test data from response
        const clonedTest = response.data || response;

        // Determine the redirect URL based on quiz mode
        let redirectUrl = "";
        if (clonedTest && clonedTest.id) {
          const quizMode = clonedTest.quiz_mode || 1;
          const answeredCount = clonedTest.answered_questions_count || 0;
          const totalCount =
            clonedTest.total_questions_count || clonedTest.questions_count || 0;

          if (quizMode == 1) {
            redirectUrl = `/qbank-quiz-tutor?qref=${clonedTest.id}&num=${answeredCount}&mode=${quizMode}&total=${totalCount}&subscriptionId=${this.subscriptionPlanId}`;
          } else {
            redirectUrl = `/qbank-quiz-timed?qref=${clonedTest.id}&num=${answeredCount}&mode=${quizMode}&total=${totalCount}&subscriptionId=${this.subscriptionPlanId}`;
          }
        }

        // Show success message
        const message =
          this.$t("qbank.new_test.test_started") ||
          "Test started successfully!";
        Swal.fire({
          icon: "success",
          title: message,
          text:
            this.$t("qbank.new_test.redirecting_to_test") ||
            "Redirecting to test...",
          timer: 1500,
          showConfirmButton: false,
        }).then(() => {
          // Navigate directly to the test if we have the URL, otherwise go to list
          if (redirectUrl) {
            this.$router.push(redirectUrl);
          } else {
            // Fallback to quiz list if no test data
            this.$router.push({
              path: "/qbank-list-quiz",
              query: { subscriptionId: this.subscriptionPlanId },
            });
          }
        });
      } catch (error) {
        console.error("Failed to clone QBank:", error);
        Swal.fire({
          icon: "error",
          title: this.$t("common.error"),
          text: error.message || this.$t("qbank.new_test.clone_error"),
        });
      } finally {
        this.cloning = false;
      }
    },
  },
};
</script>

<style scoped>
.predefined-tests-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 50%, #f8fafc 100%);
}

.page-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  padding: 24px 20px;
  color: white;
}

.page-title {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
}

.page-subtitle {
  font-size: 0.85rem;
  opacity: 0.9;
  margin: 5px 0 0;
}

.btn-back {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.3);
  color: white;
}

.btn-back i {
  color: white !important;
}

/* Filters Section */
.filters-section {
  background: white;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 100;
}

.type-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.type-chip {
  padding: 8px 18px;
  border-radius: 25px;
  border: 2px solid #e2e8f0;
  background: white;
  color: #64748b;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.type-chip:hover {
  border-color: #00c744;
  color: #00c744;
}

.type-chip.active {
  background: #00c744;
  border-color: #00c744;
  color: white;
}

.dropdown-filters {
  display: flex;
  gap: 12px;
}

.filter-group {
  flex: 1;
}

.filter-select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  color: #2d3748;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.filter-select:focus {
  outline: none;
  border-color: #00c744;
  box-shadow: 0 0 0 3px rgba(0, 199, 68, 0.1);
}

/* Content Section */
.content-section {
  padding: 24px 0;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #64748b;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
  padding: 40px 20px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.empty-icon i {
  font-size: 32px;
  color: #94a3b8;
}

.empty-state h4 {
  color: #2d3748;
  font-weight: 700;
  margin-bottom: 8px;
}

.empty-state p {
  color: #64748b;
  margin: 0;
}

/* QBanks Grid */
.qbanks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.qbank-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  display: flex;
  flex-direction: column;
  min-height: 220px;
}

.qbank-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 199, 68, 0.15);
  border-color: #00c744;
}

/* Used/Disabled Card Styles */
.qbank-card.is-used {
  opacity: 0.7;
  cursor: pointer;
  background: #f8fafc;
  border-color: #e2e8f0;
}

.qbank-card.is-used:hover {
  transform: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border-color: #e2e8f0;
}

.qbank-card.is-used .card-body,
.qbank-card.is-used .card-footer {
  filter: grayscale(50%);
}

.qbank-card.is-used .questions-badge {
  background: #94a3b8;
}

.qbank-card.is-used .btn-start {
  background: #94a3b8;
  cursor: pointer;
}

.card-header-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.used-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #64748b 0%, #475569 100%);
  color: white;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  width: fit-content;
}

.used-badge i {
  font-size: 0.8rem;
}

.qbank-card .card-header {
  padding: 18px 20px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  background: transparent;
  border: none;
}

.qbank-name {
  font-size: 1rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
  line-height: 1.4;
}

.questions-badge {
  background: #00c744;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.qbank-card .card-body {
  padding: 0 20px 18px;
  flex: 1;
}

.qbank-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.type-badge {
  background: #f0fdf4;
  color: #15803d;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
}

.category-text {
  color: #64748b;
  font-size: 0.8rem;
}

.sources-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.source-tag {
  background: #f1f5f9;
  color: #475569;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
}

.qbank-card .card-footer {
  padding: 0 20px 18px;
  background: transparent;
  border: none;
}

.btn-start {
  width: 100%;
  padding: 12px 20px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border: none;
  border-radius: 10px;
  color: white;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 199, 68, 0.3);
}

.btn-start:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.4);
  color: white;
}

/* Load More */
.load-more-container {
  display: flex;
  justify-content: center;
  padding: 30px 0;
}

.btn-load-more {
  padding: 12px 40px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 25px;
  color: #64748b;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-load-more:hover:not(:disabled) {
  border-color: #00c744;
  color: #00c744;
}

.btn-load-more:disabled {
  opacity: 0.6;
}

/* Clone Modal */
.clone-modal {
  border-radius: 20px;
  border: none;
  overflow: hidden;
}

.clone-modal .modal-header {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  color: white;
  border: none;
  padding: 18px 24px;
}

.clone-modal .modal-header .btn-close {
  filter: brightness(0) invert(1);
}

.clone-modal .modal-body {
  padding: 30px 24px;
}

.confirm-icon {
  width: 70px;
  height: 70px;
  background: #00c744;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.confirm-icon i {
  font-size: 28px;
  color: white !important;
}

.confirm-text {
  font-size: 1rem;
  color: #475569;
  margin-bottom: 16px;
}

.qbank-details {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #64748b;
  font-size: 0.9rem;
}

.detail-item i {
  color: #00c744 !important;
}

/* Force white icons */
.btn-back i,
.btn-start i,
.btn-confirm i,
.empty-icon i {
  color: white !important;
}

.empty-icon {
  background: #00c744;
}

.empty-icon i {
  color: white !important;
}

.clone-modal .modal-footer {
  border: none;
  padding: 16px 24px;
  background: #f8fafc;
  gap: 12px;
}

.clone-modal .btn-cancel {
  flex: 1;
  padding: 12px 20px;
  background: #f1f5f9;
  border: none;
  border-radius: 10px;
  color: #64748b;
  font-weight: 600;
}

.clone-modal .btn-cancel:hover {
  background: #e2e8f0;
}

.clone-modal .btn-confirm {
  flex: 2;
  padding: 12px 20px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border: none;
  border-radius: 10px;
  color: white;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(0, 199, 68, 0.3);
}

.clone-modal .btn-confirm:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 199, 68, 0.4);
  color: white;
}

.clone-modal .btn-confirm:disabled {
  opacity: 0.6;
}

/* RTL Support */
[dir="rtl"] .btn-back i,
[dir="rtl"] .btn-start i,
[dir="rtl"] .btn-confirm i {
  margin-right: 0;
  margin-left: 8px;
}

/* Dark Mode */
[data-bs-theme="dark"] .predefined-tests-page,
.dark .predefined-tests-page {
  background: linear-gradient(180deg, #1a1a2e 0%, #16213e 100%);
}

[data-bs-theme="dark"] .filters-section,
.dark .filters-section {
  background: #1e293b;
}

[data-bs-theme="dark"] .type-chip,
.dark .type-chip {
  background: #334155;
  border-color: #475569;
  color: #94a3b8;
}

[data-bs-theme="dark"] .type-chip:hover,
.dark .type-chip:hover {
  border-color: #00c744;
  color: #4ade80;
}

[data-bs-theme="dark"] .filter-select,
.dark .filter-select {
  background: #334155;
  border-color: #475569;
  color: #e2e8f0;
}

[data-bs-theme="dark"] .qbank-card,
.dark .qbank-card {
  background: #1e293b;
}

[data-bs-theme="dark"] .qbank-card.is-used,
.dark .qbank-card.is-used {
  background: #0f172a;
  border-color: #334155;
}

[data-bs-theme="dark"] .used-badge,
.dark .used-badge {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
}

[data-bs-theme="dark"] .qbank-name,
.dark .qbank-name {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .empty-icon,
.dark .empty-icon {
  background: #334155;
}

[data-bs-theme="dark"] .btn-load-more,
.dark .btn-load-more {
  background: #1e293b;
  border-color: #475569;
  color: #94a3b8;
}

[data-bs-theme="dark"] .clone-modal,
.dark .clone-modal {
  background: #1e293b;
}

[data-bs-theme="dark"] .clone-modal .modal-footer,
.dark .clone-modal .modal-footer {
  background: #1e293b;
}

/* Responsive */
@media (max-width: 768px) {
  .page-header {
    padding: 20px 15px;
  }

  .page-title {
    font-size: 1.25rem;
  }

  .dropdown-filters {
    flex-direction: column;
  }

  .qbanks-grid {
    grid-template-columns: 1fr;
  }
}
</style>
